import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calculator, ArrowRight, Info } from 'lucide-react';

const PRODUCTS = [
  { id: 'geobar-b', name: 'GEObar B (BFRP)', pricePerFt: 2.50 },
  { id: 'geobar-fg', name: 'GEObar FG (GFRP)', pricePerFt: 1.85 },
];

const BAR_SIZES = [
  { size: '#3', diameter: '3/8"', weightPerFt: 0.09, multiplier: 0.75 },
  { size: '#4', diameter: '1/2"', weightPerFt: 0.13, multiplier: 1.0 },
  { size: '#5', diameter: '5/8"', weightPerFt: 0.20, multiplier: 1.35 },
  { size: '#6', diameter: '3/4"', weightPerFt: 0.29, multiplier: 1.75 },
];

export default function QuoteCalculatorPage() {
  const [product, setProduct] = useState('geobar-b');
  const [barSize, setBarSize] = useState('#4');
  const [quantity, setQuantity] = useState(100);
  const [barLength, setBarLength] = useState(20);

  const selectedProduct = PRODUCTS.find(p => p.id === product);
  const selectedBar = BAR_SIZES.find(b => b.size === barSize);
  
  const totalLinearFeet = quantity * barLength;
  const pricePerFoot = selectedProduct.pricePerFt * selectedBar.multiplier;
  const estimatedTotal = totalLinearFeet * pricePerFoot;
  const steelEquivalent = totalLinearFeet * 0.45; // Steel rebar approx price
  const savings = ((estimatedTotal - steelEquivalent) / steelEquivalent * 100).toFixed(0);

  return (
    <div className="bg-charcoal min-h-screen pt-20" data-testid="quote-calculator-page">
      {/* Hero */}
      <section className="py-16 lg:py-24 border-b border-border-dark">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-4 mb-4">
              <Calculator size={24} className="text-gold" />
              <span className="tech-label">//ESTIMATE_PRICING</span>
            </div>
            <h1 className="heading-display text-stone text-5xl md:text-6xl mb-6">
              Quote <span className="text-gold">Calculator</span>
            </h1>
            <p className="text-stone/50 max-w-2xl text-lg">
              Get a ballpark estimate for your project. Final pricing depends on quantity, 
              delivery location, and current material costs.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Calculator */}
      <section className="py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Input Panel */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="space-y-8"
            >
              {/* Product Selection */}
              <div>
                <label className="block text-stone text-sm mb-4">Product Type</label>
                <div className="grid grid-cols-1 gap-3">
                  {PRODUCTS.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setProduct(p.id)}
                      className={`p-4 text-left border transition-all ${
                        product === p.id 
                          ? 'border-gold bg-gold/10' 
                          : 'border-border-dark hover:border-gold/30'
                      }`}
                    >
                      <span className="text-stone font-mono">{p.name}</span>
                      <span className="text-stone/40 text-sm ml-2">${p.pricePerFt.toFixed(2)}/ft base</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Bar Size */}
              <div>
                <label className="block text-stone text-sm mb-4">Bar Size</label>
                <div className="grid grid-cols-4 gap-3">
                  {BAR_SIZES.map(bar => (
                    <button
                      key={bar.size}
                      type="button"
                      onClick={() => setBarSize(bar.size)}
                      className={`p-4 text-center border transition-all ${
                        barSize === bar.size 
                          ? 'border-gold bg-gold/10' 
                          : 'border-border-dark hover:border-gold/30'
                      }`}
                    >
                      <span className="text-stone font-mono block">{bar.size}</span>
                      <span className="text-stone/40 text-xs">{bar.diameter}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Quantity */}
              <div>
                <label className="block text-stone text-sm mb-4">Number of Bars</label>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 0))}
                  className="input text-2xl font-mono"
                  min="1"
                />
              </div>

              {/* Length */}
              <div>
                <label className="block text-stone text-sm mb-4">Bar Length (feet)</label>
                <div className="grid grid-cols-4 gap-3">
                  {[10, 20, 30, 40].map(len => (
                    <button
                      key={len}
                      type="button"
                      onClick={() => setBarLength(len)}
                      className={`p-4 text-center border transition-all ${
                        barLength === len 
                          ? 'border-gold bg-gold/10' 
                          : 'border-border-dark hover:border-gold/30'
                      }`}
                    >
                      <span className="text-stone font-mono">{len} ft</span>
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Results Panel */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="border border-gold/30 bg-surface-dark/50 p-8 sticky top-28">
                <h2 className="heading-section text-gold text-xl mb-8">Estimate Summary</h2>

                <div className="space-y-6 mb-8">
                  <div className="flex justify-between items-baseline border-b border-border-dark pb-4">
                    <span className="text-stone/60">Product</span>
                    <span className="text-stone font-mono">{selectedProduct.name}</span>
                  </div>
                  <div className="flex justify-between items-baseline border-b border-border-dark pb-4">
                    <span className="text-stone/60">Bar Size</span>
                    <span className="text-stone font-mono">{barSize} ({selectedBar.diameter})</span>
                  </div>
                  <div className="flex justify-between items-baseline border-b border-border-dark pb-4">
                    <span className="text-stone/60">Quantity</span>
                    <span className="text-stone font-mono">{quantity.toLocaleString()} bars</span>
                  </div>
                  <div className="flex justify-between items-baseline border-b border-border-dark pb-4">
                    <span className="text-stone/60">Total Linear Feet</span>
                    <span className="text-stone font-mono">{totalLinearFeet.toLocaleString()} ft</span>
                  </div>
                  <div className="flex justify-between items-baseline border-b border-border-dark pb-4">
                    <span className="text-stone/60">Price per Foot</span>
                    <span className="text-stone font-mono">${pricePerFoot.toFixed(2)}</span>
                  </div>
                </div>

                <div className="bg-charcoal p-6 mb-6">
                  <span className="text-stone/60 text-sm block mb-2">Estimated Total</span>
                  <span className="heading-display text-5xl text-gold">
                    ${estimatedTotal.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </span>
                </div>

                <div className="p-4 border border-gold/20 bg-gold/5 mb-8">
                  <div className="flex items-start gap-2">
                    <Info size={16} className="text-gold mt-0.5" />
                    <div className="text-sm">
                      <p className="text-gold mb-1">Lifecycle Value</p>
                      <p className="text-stone/60">
                        While upfront cost is ~{Math.abs(parseInt(savings))}% {parseInt(savings) > 0 ? 'higher' : 'lower'} than steel, 
                        FRP eliminates corrosion maintenance and lasts 4× longer.
                      </p>
                    </div>
                  </div>
                </div>

                <Link to="/contact" className="btn btn-primary w-full" data-testid="get-formal-quote">
                  Get Formal Quote
                  <ArrowRight size={16} className="ml-2" />
                </Link>

                <p className="text-stone/30 text-xs mt-4 text-center">
                  *Estimates are approximate. Contact us for project-specific pricing.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
